import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ROLES } from '../mock';
import { computeScores } from '../lib/scoring';
import { fmtDate, scoreColor, daysUntil } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, ClipboardList, AlertCircle, ArrowRight, TrendingUp, UserCheck } from 'lucide-react';
import AlertsFeed from '../components/AlertsFeed';
import AuditExportButton from '../components/AuditExportButton';
import DigestSendButton from '../components/DigestSendButton';
import DigestAnalytics from '../components/DigestAnalytics';
import RMLeaderboard from '../components/RMLeaderboard';

const StatCard = ({ icon: Icon, label, value, accent, hint }) => (
  <Card className="border-brand-border">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="text-3xl font-bold text-brand-indigo mt-2">{value}</div>
          {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SalesHeadDashboardPage = () => {
  const { user, USERS: allUsers, visibleClients, visibleSessions } = useApp();
  const mappedRms = (user?.rm_ids || []).map((id) => allUsers.find((u) => u.user_id === id)).filter(Boolean);

  const now = new Date();
  const thisMonth = visibleSessions.filter((s) => {
    const d = new Date(s.session_date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const followupsWeek = visibleSessions.filter((s) => { const du = daysUntil(s.follow_up_date); return du !== null && du >= 0 && du <= 7; });
  const avgScore = visibleSessions.length ? Math.round(visibleSessions.reduce((a, s) => a + computeScores(s).overall, 0) / visibleSessions.length) : 0;

  const rmRows = mappedRms.map((rm) => {
    const rmClients = visibleClients.filter((c) => c.rm_id === rm.user_id);
    const rmSessions = visibleSessions.filter((s) => s.rm_id === rm.user_id);
    const avg = rmSessions.length ? Math.round(rmSessions.reduce((a, s) => a + computeScores(s).overall, 0) / rmSessions.length) : 0;
    return { rm, clients: rmClients.length, sessions: rmSessions.length, avg, latest: rmSessions.sort((a, b) => new Date(b.session_date) - new Date(a.session_date))[0] };
  });

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Sales Head overview</div>
          <h1 className="text-3xl font-bold text-brand-indigo">{user?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.title}</p>
        </div>
        <div className="flex items-center gap-2">
          <DigestSendButton />
          <AuditExportButton />
          <Badge variant="outline" className="text-brand-indigo border-brand-border bg-white">Team view · {mappedRms.length} RM{mappedRms.length !== 1 && 's'}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={UserCheck} label="Mapped RMs" value={mappedRms.length} accent="bg-brand-cyan" />
        <StatCard icon={Users} label="Team Clients" value={visibleClients.length} accent="bg-brand-indigo" />
        <StatCard icon={ClipboardList} label="Sessions This Month" value={thisMonth.length} accent="bg-brand-amber" />
        <StatCard icon={TrendingUp} label="Team Avg Score" value={avgScore || '—'} accent="bg-brand-green" />
      </div>

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-brand-border">
            <div className="text-sm font-semibold text-brand-indigo">My RMs</div>
            <div className="text-[11px] text-muted-foreground">Book size, throughput and average MRI score</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                  <th className="px-5 py-3 font-semibold">RM</th>
                  <th className="px-5 py-3 font-semibold">EUIN</th>
                  <th className="px-5 py-3 font-semibold text-right">Clients</th>
                  <th className="px-5 py-3 font-semibold text-right">Sessions</th>
                  <th className="px-5 py-3 font-semibold text-right">Avg Score</th>
                  <th className="px-5 py-3 font-semibold">Latest Session</th>
                </tr>
              </thead>
              <tbody>
                {rmRows.map(({ rm, clients, sessions, avg, latest }) => {
                  const c = scoreColor(avg);
                  return (
                    <tr key={rm.user_id} className="border-b border-brand-border/50 hover:bg-brand-bg">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-brand-indigo">{rm.name}</div>
                        <div className="text-[11px] text-muted-foreground">{rm.email}</div>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{rm.euin}</td>
                      <td className="px-5 py-3 text-right font-semibold">{clients}</td>
                      <td className="px-5 py-3 text-right font-semibold">{sessions}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={`h-2 w-2 rounded-full ${c.bg}`} />
                          <span className={`font-bold ${c.text}`}>{avg || '—'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{latest ? fmtDate(latest.session_date) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-brand-indigo">Recent Team Sessions</div>
              <div className="text-[11px] text-muted-foreground">Across your mapped RMs</div>
            </div>
            <Link to="/clients" className="text-xs text-brand-indigo font-semibold hover:underline flex items-center gap-1">All clients <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                  <th className="px-5 py-3 font-semibold">Client</th>
                  <th className="px-5 py-3 font-semibold">RM</th>
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Score</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {[...visibleSessions].sort((a, b) => new Date(b.session_date) - new Date(a.session_date)).slice(0, 8).map((s) => {
                  const client = visibleClients.find((c) => c.id === s.client_id);
                  const rm = allUsers.find((u) => u.user_id === s.rm_id);
                  const sc = computeScores(s);
                  const c = scoreColor(sc.overall);
                  return (
                    <tr key={s.id} className="border-b border-brand-border/50 hover:bg-brand-bg">
                      <td className="px-5 py-3 font-medium text-brand-indigo">{client?.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{rm?.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{fmtDate(s.session_date)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${c.bg}`} />
                          <span className={`font-semibold ${c.text}`}>{sc.overall}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/clients/${s.client_id}/mri/${s.id}`} className="text-xs text-brand-indigo font-semibold hover:underline">View</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <RMLeaderboard />

      <AlertsFeed compact />

      <DigestAnalytics />

      <Card className="border-brand-border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-brand-amber" />
            <div className="text-sm font-semibold text-brand-indigo">Follow-ups Due This Week</div>
          </div>
          {followupsWeek.length === 0 && <div className="text-sm text-muted-foreground">No follow-ups scheduled in the next 7 days.</div>}
          <div className="space-y-2">
            {followupsWeek.slice(0, 8).map((s) => {
              const client = visibleClients.find((c) => c.id === s.client_id);
              const rm = allUsers.find((u) => u.user_id === s.rm_id);
              return (
                <Link key={s.id} to={`/clients/${s.client_id}`} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-brand-bg">
                  <div>
                    <div className="text-sm font-medium text-brand-indigo">{client?.name}</div>
                    <div className="text-[11px] text-muted-foreground">RM: {rm?.name} · {fmtDate(s.follow_up_date)}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesHeadDashboardPage;
